'use strict';

const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

const BODY_MOUNT = '#SUG-Body';
const SEARCH_MOUNT = '#SUG-Search';
const RESULTS_MOUNT = '#SUG-Results';
const DETAIL_MOUNT = '#SUG-Detail';
const PICKER_HASH = 'SUG-CustomerPicker';
const PICKER_MOUNT = '#SUG-CustomerPicker';

const GRANT_HASH = 'Permission-Grant';

// Elevating a user to "can manage configuration for their customer". The api/config write gate
// (_hasCustomerScopedConfigWriteAccess) passes for anyone holding a role/group whose PermissionLevel is
// 'CustomerAdmin' — it never inspects individual permission grants. So this tool find-or-creates a managed,
// per-customer Permission Group that carries that PermissionLevel and assigns it to the user. No server change:
// the user's session then carries a PermissionLevel === 'CustomerAdmin' entry and the config gate passes.
const CONFIG_ADMIN_LEVEL = 'CustomerAdmin';
const CONFIG_ADMIN_GROUP_NAME = 'Customer Config Admin';

// User.IDRole is the base permission LEVEL (0-5) — the strongest "likely config admin" signal. Names mirror the
// legacy user admin (RoleSelector.vue); shown on the "all users" list and used to rank it (seniors first).
const USER_LEVEL_NAMES = { 0: 'Read Only', 1: 'Field Personnel', 2: 'Office Staff', 3: 'Office Manager', 4: 'Executive Manager', 5: 'Pavia Administrator' };

/**
 * SuperUserGrant — provider behind the #/GrantAccess screen (a bespoke, platform-super-user-only bootstrap tool).
 *
 * What it does:
 *   1. Searches ALL users across every customer (a super-user's Meadow queries are not customer-scoped) by name or
 *      email — three parallel LIKE queries merged and de-duplicated.
 *   2. Grants a selected user `Permission-Grant`, scoped to that user's OWN customer, via the direct grant-to-user
 *      endpoint: GET Permissions/Grant/Permission-Grant/To/User/<IDUser>/For/Customer/<IDCustomer>. That writes a
 *      single UserPermission override row (idempotent). Revoking deletes that row (a clean reset to the default).
 *
 * Everything is gated to a customer-1 / role-index-5 super-user client-side; the server is authoritative (its own
 * grant handler re-checks that the caller holds Permission-Grant in the target context, which a super-user does).
 *
 * Render model mirrors Entitlements-Provider: hand-built HTML assigned into mounts. The search <input> is built
 * ONCE into #SUG-Body and never re-assigned, so keystroke re-renders (which only touch #SUG-Results / #SUG-Detail)
 * don't steal focus from it.
 */
class SuperUserGrantProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._pickerBuilt = false;
		this._customerPicker = null;
		this._searchTimer = null;
		this._searchToken = 0;
	}

	get app() { return this.pict.PictApplication; }
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.SuperUserGrant)
		{
			this.pict.AppData.SuperUserGrant =
			{
				Loaded: false, Loading: false, CustomersByID: {},
				Query: '', Searching: false, Results: [], CustomerFilter: null,
				SelectedUser: null, GrantLoading: false, GrantRecord: null,
				ConfigAdminLoading: false, ConfigAdminGroup: null, ConfigAdminHeld: null,
				Busy: false, Error: '', Notice: '',
				ShowingAll: false, ListLoading: false, ListedCustomer: null,
			};
		}
		return this.pict.AppData.SuperUserGrant;
	}

	/** Customer-1 / role-index-5 super-user — the only session allowed to use this tool. Server stays authoritative. */
	isAuthorized()
	{
		if (this.app && typeof this.app._isSuperUser === 'function') { return this.app._isSuperUser(); }
		const tmpUser = this.pict.AppData.CurrentUser;
		return !!(tmpUser && (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1));
	}

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pB) => resolve(pErr ? null : pB))) : Promise.resolve(null); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pB) => resolve(pErr ? null : pB))) : Promise.resolve(null); }

	// ── Load (customer name map, for display) ────────────────────────────────────

	loadAsync()
	{
		const tmpState = this.state;
		if (tmpState.Loading || !this.isAuthorized()) { return; }
		tmpState.Loading = true;
		this._getJSON('Customers/0/2000').then((pCustomers) =>
		{
			const tmpMap = {};
			(Array.isArray(pCustomers) ? pCustomers : []).forEach((pCustomer) =>
			{
				if (pCustomer && (pCustomer.IDCustomer != null)) { tmpMap[Number(pCustomer.IDCustomer)] = pCustomer.Name || pCustomer.CustomerName || ''; }
			});
			tmpState.CustomersByID = tmpMap;
			tmpState.Loaded = true;
			tmpState.Loading = false;
			this.render();
		});
	}

	_customerName(pIDCustomer)
	{
		const tmpName = this.state.CustomersByID[Number(pIDCustomer)];
		return tmpName ? `${tmpName} (#${Number(pIDCustomer)})` : `Customer #${Number(pIDCustomer)}`;
	}

	// ── Search (three parallel LIKE queries, merged) ─────────────────────────────

	onSearchInput(pValue)
	{
		const tmpState = this.state;
		tmpState.Query = String(pValue == null ? '' : pValue);
		if (this._searchTimer) { clearTimeout(this._searchTimer); }
		this._searchTimer = setTimeout(() => { this._searchTimer = null; this.search(); }, 250);
	}

	/** Scope the search to a customer (or clear back to all). Fired by the customer picker's OnChange. */
	onCustomerChange(pValue)
	{
		const tmpState = this.state;
		tmpState.CustomerFilter = (pValue === null || pValue === undefined || pValue === '' || (Number(pValue) === 0)) ? null : Number(pValue);
		this.search();
	}

	/**
	 * Pre-scope the search to one customer — the deep-link entry point from the Customers screen's
	 * "grant one in Grants first" action (#/GrantAccess/:IDCustomer). Sets the filter and syncs the
	 * customer picker so the super-user lands with that customer already selected.
	 * @param {number} pIDCustomer
	 */
	applyCustomerScope(pIDCustomer)
	{
		const tmpID = Number(pIDCustomer);
		if (!tmpID) { return; }
		this.state.CustomerFilter = tmpID;
		// Not loaded yet → loadAsync()'s render builds the picker already honoring state.CustomerFilter.
		if (!this.state.Loaded && !this.state.Loading) { this.loadAsync(); }
		// Already loaded → push the value into the existing picker.
		else if (this._customerPicker && (typeof this._customerPicker.setValue === 'function')) { this._customerPicker.setValue(tmpID); }
		// Auto-list this customer's users (most likely admins first) so setup needs no name-guessing.
		this.loadCustomerUsers();
	}

	/**
	 * Load every (non-deleted) user in the scoped customer, ranked most-likely-admin first (senior base level,
	 * then most recently active), so a super-user can pick the config admin without knowing a name. No-op without
	 * a customer scope. Cached per customer; re-fetches when the scope changes.
	 */
	loadCustomerUsers()
	{
		const tmpState = this.state;
		if (!this.isAuthorized() || !tmpState.CustomerFilter) { return; }
		const tmpID = Number(tmpState.CustomerFilter);
		tmpState.ShowingAll = true;
		// Already have this customer's list loaded → just show it (no refetch).
		if ((Number(tmpState.ListedCustomer) === tmpID) && tmpState.Results.length && !tmpState.ListLoading) { this.render(); return; }
		tmpState.ListLoading = true;
		tmpState.Results = [];
		this.render();
		const tmpToken = ++this._searchToken;   // supersede any in-flight keystroke search
		// Server-sort by base level DESC so the cap keeps the seniors on large customers; refined client-side below.
		this._getJSON(`Users/FilteredTo/FBV~IDCustomer~EQ~${tmpID}~FBV~Deleted~EQ~0~FSF~IDRole~DESC~0/0/200`).then((pRows) =>
		{
			if (tmpToken !== this._searchToken) { return; }   // a newer search/list superseded this one
			const tmpUsers = (Array.isArray(pRows) ? pRows : []).slice();
			tmpUsers.sort((pA, pB) =>
				(this._adminRank(pB) - this._adminRank(pA))
				|| (this._loginTime(pB) - this._loginTime(pA))
				|| String(pA.NameLast || '').localeCompare(String(pB.NameLast || ''))
				|| String(pA.NameFirst || '').localeCompare(String(pB.NameFirst || '')));
			tmpState.Results = tmpUsers;
			tmpState.ListedCustomer = tmpID;
			tmpState.ListLoading = false;
			this.render();
		});
	}

	/** Likely-config-admin rank: senior base level first; disabled users sink to the bottom. */
	_adminRank(pUser)
	{
		if (!pUser) { return -10000; }
		let tmpRank = Number(pUser.IDRole || 0);
		if (pUser.Disabled === true || Number(pUser.Disabled) === 1) { tmpRank -= 1000; }
		return tmpRank;
	}

	/** Epoch ms of the user's last login (0 when never / unparseable) — a recency tiebreak for the ranking. */
	_loginTime(pUser) { const tmpTime = (pUser && pUser.LastLoginTime) ? Date.parse(pUser.LastLoginTime) : 0; return tmpTime || 0; }

	search()
	{
		const tmpState = this.state;
		if (!this.isAuthorized()) { return; }
		const tmpQuery = String(tmpState.Query || '').trim();
		if (tmpQuery.length < 2)
		{
			tmpState.Searching = false;
			// Empty query + a customer in scope → show that customer's users (ranked) instead of a dead end.
			if (tmpState.CustomerFilter) { this.loadCustomerUsers(); return; }
			tmpState.ShowingAll = false;
			tmpState.Results = [];
			this.render();
			return;
		}
		tmpState.ShowingAll = false;
		const tmpToken = ++this._searchToken;
		tmpState.Searching = true;
		this.render();
		const tmpScope = tmpState.CustomerFilter ? `FBV~IDCustomer~EQ~${Number(tmpState.CustomerFilter)}` : '';
		libUserSearch.searchUsersAsync((pURL) => this._getJSON(pURL), tmpQuery, { Scope: tmpScope, PageSize: 50 }).then((pUsers) =>
		{
			if (tmpToken !== this._searchToken)
			{
				return;   // a newer search superseded this one
			}
			tmpState.Results = pUsers;
			tmpState.Searching = false;
			this.render();
		});
	}

	// ── Select a user + load its current Permission-Grant state ──────────────────

	selectUser(pIDUser)
	{
		const tmpState = this.state;
		const tmpUser = (tmpState.Results || []).find((pItem) => Number(pItem.IDUser) === Number(pIDUser)) || null;
		tmpState.SelectedUser = tmpUser;
		tmpState.GrantRecord = null;
		tmpState.ConfigAdminGroup = null;
		tmpState.ConfigAdminHeld = null;
		tmpState.Error = '';
		tmpState.Notice = '';
		tmpState.GrantLoading = !!tmpUser;
		tmpState.ConfigAdminLoading = !!tmpUser;
		this.render();
		if (tmpUser) { this._loadGrantState(tmpUser); this._loadConfigAdminState(tmpUser); }
	}

	/**
	 * Load whether the user already holds the managed "Customer Config Admin" group (PermissionLevel
	 * 'CustomerAdmin') in their own customer — i.e. whether they can already manage configuration.
	 */
	_loadConfigAdminState(pUser)
	{
		const tmpState = this.state;
		const tmpIDUser = Number(pUser.IDUser);
		const tmpIDCustomer = Number(pUser.IDCustomer);
		this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${tmpIDCustomer}~FBV~Name~EQ~${encodeURIComponent(CONFIG_ADMIN_GROUP_NAME)}~FBV~Deleted~EQ~0/0/1`).then((pRoles) =>
		{
			if (!tmpState.SelectedUser || (Number(tmpState.SelectedUser.IDUser) !== tmpIDUser)) { return; }
			const tmpGroup = (Array.isArray(pRoles) ? pRoles : []).find((pRole) => pRole && pRole.IDRole) || null;
			tmpState.ConfigAdminGroup = tmpGroup;
			if (!tmpGroup) { tmpState.ConfigAdminHeld = null; tmpState.ConfigAdminLoading = false; this.render(); return; }
			this._getJSON(`UserRoleJoins/FilteredTo/FBV~IDUser~EQ~${tmpIDUser}~FBV~IDRole~EQ~${Number(tmpGroup.IDRole)}`).then((pJoins) =>
			{
				if (!tmpState.SelectedUser || (Number(tmpState.SelectedUser.IDUser) !== tmpIDUser)) { return; }
				tmpState.ConfigAdminHeld = (Array.isArray(pJoins) ? pJoins : []).find((pJoin) => pJoin && pJoin.IDUserRoleJoin) || null;
				tmpState.ConfigAdminLoading = false;
				this.render();
			});
		});
	}

	/**
	 * Find (or create) the per-customer managed "Customer Config Admin" Permission Group carrying PermissionLevel
	 * 'CustomerAdmin'. Resolves the group record, or null on failure.
	 * @param {number} pIDCustomer
	 */
	_ensureConfigAdminGroup(pIDCustomer)
	{
		return this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${pIDCustomer}~FBV~Name~EQ~${encodeURIComponent(CONFIG_ADMIN_GROUP_NAME)}~FBV~Deleted~EQ~0/0/1`).then((pRoles) =>
		{
			const tmpExisting = (Array.isArray(pRoles) ? pRoles : []).find((pRole) => pRole && pRole.IDRole);
			if (tmpExisting) { return tmpExisting; }
			return this._postJSON('Role',
				{
					Name: CONFIG_ADMIN_GROUP_NAME, Type: 'PermissionGroup', PermissionLevel: CONFIG_ADMIN_LEVEL,
					Description: 'Grants configuration-admin rights (PermissionLevel CustomerAdmin) for this customer — approval workflows, cascading configuration, etc. Managed by Grant Access.',
					IDCustomer: pIDCustomer,
				});
		});
	}

	_loadGrantState(pUser)
	{
		const tmpState = this.state;
		const tmpIDUser = Number(pUser.IDUser);
		const tmpIDCustomer = Number(pUser.IDCustomer);
		const tmpURL = `UserPermissions/FilteredTo/FBV~IDUser~EQ~${tmpIDUser}~FBV~Hash~EQ~${GRANT_HASH}~FBV~Context~EQ~Customer~FBV~IDContext~EQ~${tmpIDCustomer}`;
		this._getJSON(tmpURL).then((pRows) =>
		{
			if (!tmpState.SelectedUser || (Number(tmpState.SelectedUser.IDUser) !== tmpIDUser)) { return; }
			tmpState.GrantRecord = (Array.isArray(pRows) ? pRows : []).find((pRow) => pRow && pRow.Allowed) || null;
			tmpState.GrantLoading = false;
			this.render();
		});
	}

	// ── Grant / revoke ───────────────────────────────────────────────────────────

	grant()
	{
		const tmpState = this.state;
		const tmpUser = tmpState.SelectedUser;
		if (!this.isAuthorized() || !tmpUser || tmpState.Busy) { return; }
		tmpState.Busy = true;
		tmpState.Error = '';
		tmpState.Notice = '';
		this.render();
		const tmpIDUser = Number(tmpUser.IDUser);
		const tmpIDCustomer = Number(tmpUser.IDCustomer);
		this._getJSON(`Permissions/Grant/${GRANT_HASH}/To/User/${tmpIDUser}/For/Customer/${tmpIDCustomer}`).then((pResult) =>
		{
			tmpState.Busy = false;
			if (!pResult)
			{
				tmpState.Error = 'The server rejected the grant. Nothing was changed.';
				this.render();
				return;
			}
			tmpState.Notice = `Granted Permission-Grant to ${libUserSearch.userLabel(tmpUser)} in ${this._customerName(tmpIDCustomer)}.`;
			this._loadGrantState(tmpUser);
		});
	}

	revoke()
	{
		const tmpState = this.state;
		const tmpUser = tmpState.SelectedUser;
		const tmpRecord = tmpState.GrantRecord;
		const tmpRC = this.restClient;
		if (!this.isAuthorized() || !tmpUser || !tmpRecord || tmpState.Busy) { return; }
		if (!tmpRC || (typeof tmpRC.deleteEntity !== 'function')) { return; }
		tmpState.Busy = true;
		tmpState.Error = '';
		tmpState.Notice = '';
		this.render();
		tmpRC.deleteEntity('UserPermission', tmpRecord.IDUserPermission, (pErr) =>
		{
			tmpState.Busy = false;
			if (pErr)
			{
				tmpState.Error = 'The server rejected the removal. Nothing was changed.';
				this.render();
				return;
			}
			tmpState.Notice = `Removed Permission-Grant from ${libUserSearch.userLabel(tmpUser)}.`;
			this._loadGrantState(tmpUser);
		});
	}

	// ── Config-admin: elevate a user to manage configuration for their own customer ──

	grantConfigAdmin()
	{
		const tmpState = this.state;
		const tmpUser = tmpState.SelectedUser;
		if (!this.isAuthorized() || !tmpUser || tmpState.Busy) { return; }
		tmpState.Busy = true;
		tmpState.Error = '';
		tmpState.Notice = '';
		this.render();
		const tmpIDUser = Number(tmpUser.IDUser);
		const tmpIDCustomer = Number(tmpUser.IDCustomer);
		this._ensureConfigAdminGroup(tmpIDCustomer).then((tmpGroup) =>
		{
			if (!tmpGroup || !tmpGroup.IDRole)
			{
				tmpState.Busy = false;
				tmpState.Error = 'Could not create the Customer Config Admin group. Nothing was changed.';
				this.render();
				return;
			}
			// Assign the group to the user at the Customer scope (a UserRoleJoin) — same path #/Access uses.
			this._putJSON('Permissions/Role/For/User', { IDRole: Number(tmpGroup.IDRole), IDUser: tmpIDUser, Context: 'Customer', IDContext: tmpIDCustomer }).then((pResult) =>
			{
				tmpState.Busy = false;
				if (!pResult)
				{
					tmpState.Error = 'The server rejected the assignment. Nothing was changed.';
					this.render();
					return;
				}
				tmpState.Notice = `${libUserSearch.userLabel(tmpUser)} can now manage configuration for ${this._customerName(tmpIDCustomer)}.`;
				this._loadConfigAdminState(tmpUser);
			});
		});
	}

	revokeConfigAdmin()
	{
		const tmpState = this.state;
		const tmpUser = tmpState.SelectedUser;
		const tmpHeld = tmpState.ConfigAdminHeld;
		const tmpRC = this.restClient;
		if (!this.isAuthorized() || !tmpUser || !tmpHeld || tmpState.Busy) { return; }
		if (!tmpRC || (typeof tmpRC.deleteEntity !== 'function')) { return; }
		tmpState.Busy = true;
		tmpState.Error = '';
		tmpState.Notice = '';
		this.render();
		// Remove the user's assignment to the group (the UserRoleJoin) — the group itself is left for reuse.
		tmpRC.deleteEntity('UserRoleJoin', tmpHeld.IDUserRoleJoin, (pErr) =>
		{
			tmpState.Busy = false;
			if (pErr)
			{
				tmpState.Error = 'The server rejected the removal. Nothing was changed.';
				this.render();
				return;
			}
			tmpState.Notice = `Removed configuration administration from ${libUserSearch.userLabel(tmpUser)}.`;
			this._loadConfigAdminState(tmpUser);
		});
	}

	// ── Render ───────────────────────────────────────────────────────────────────

	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(BODY_MOUNT).length === 0) { return; }
		if (!this.isAuthorized())
		{
			this._pickerBuilt = false;
			tmpCA.assignContent(BODY_MOUNT, this._guardHTML());
			return;
		}
		// (Re)build the body shell whenever it's absent from the live DOM — first render, or a
		// re-navigation that replaced the container. Keying off the search input's presence (not a
		// sticky instance flag) keeps the mounts valid across route changes and preserves input focus
		// on the keystroke re-renders that only touch #SUG-Results / #SUG-Detail.
		if (tmpCA.getElement(SEARCH_MOUNT).length === 0)
		{
			tmpCA.assignContent(BODY_MOUNT, this._bodyShellHTML());
			this._pickerBuilt = false;   // the picker's mount is fresh — it must be recreated into it
		}
		this._ensureCustomerPicker();
		tmpCA.assignContent(RESULTS_MOUNT, this._resultsHTML());
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
	}

	/** Create the customer scope picker once the customer list is loaded and its mount exists in the DOM. */
	_ensureCustomerPicker()
	{
		if (this._pickerBuilt || !this.state.Loaded) { return; }
		const tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPickerProvider || (typeof tmpPickerProvider.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement(PICKER_MOUNT).length === 0) { return; }
		const tmpPicker = tmpPickerProvider.createPicker(PICKER_HASH,
		{
			DestinationAddress: PICKER_MOUNT,
			Placeholder: '(all)',
			AllowClear: true,
			ClearLabel: '(all)',
			Searchable: true,
			Options: this._customerOptions(),
			OnChange: (pValue) => this.onCustomerChange(pValue),
		});
		if (tmpPicker && (typeof tmpPicker.render === 'function')) { tmpPicker.render(); }
		this._customerPicker = tmpPicker;
		// Reflect a pre-set scope (arriving via #/GrantAccess/:IDCustomer) so the picker shows that customer.
		if (tmpPicker && (typeof tmpPicker.setValue === 'function') && this.state.CustomerFilter) { tmpPicker.setValue(Number(this.state.CustomerFilter)); }
		this._pickerBuilt = true;
	}

	/** The customer list as picker options ({ Value, Text }), sorted by name. The "(all)" state is the
	 * picker's own AllowClear row, so it is not an explicit option here. */
	_customerOptions()
	{
		const tmpMap = this.state.CustomersByID || {};
		return Object.keys(tmpMap)
			.map((pKey) => ({ Value: Number(pKey), Text: tmpMap[pKey] || `Customer #${pKey}` }))
			.sort((pA, pB) => String(pA.Text).localeCompare(String(pB.Text)));
	}

	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }


	_guardHTML()
	{
		return `<div class="sug-guard"><div class="sug-guard-title">Restricted tool</div>`
			+ `This bootstrap tool is available to platform super-users only.</div>`;
	}

	_bodyShellHTML()
	{
		const tmpValue = this._esc(this.state.Query || '');
		return `<div class="sug-filterbar">`
			+ `<div class="sug-filter-field sug-filter-customer"><label class="sug-filter-label">Customer</label>`
			+ `<div id="SUG-CustomerPicker" class="sug-customer-picker"></div></div>`
			+ `<div class="sug-filter-field sug-filter-search"><label class="sug-filter-label">Search users</label>`
			+ `<input id="SUG-Search" class="sug-search" type="text" autocomplete="off" spellcheck="false" `
			+ `placeholder="Search users by name, login ID or email…" value="${tmpValue}" `
			+ `oninput="_Pict.providers.SuperUserGrant.onSearchInput(this.value)" /></div>`
			+ `</div>`
			+ `<div class="sug-cols"><div id="SUG-Results" class="sug-results"></div><div id="SUG-Detail" class="sug-detail"></div></div>`;
	}

	_resultsHTML()
	{
		const tmpState = this.state;
		const tmpQuery = String(tmpState.Query || '').trim();
		const tmpSearching = (tmpQuery.length >= 2);
		if (tmpState.Searching) { return `<div class="sug-hint">Searching…</div>`; }
		if (tmpState.ListLoading) { return `<div class="sug-hint">Loading users…</div>`; }
		// Full customer list (no active search) — most likely admins first.
		if (!tmpSearching && tmpState.ShowingAll && tmpState.CustomerFilter)
		{
			if (!tmpState.Results.length) { return `<div class="sug-results-empty">No users in ${this._esc(this._customerName(tmpState.CustomerFilter))} yet.</div>`; }
			const tmpHeader = `<div class="sug-results-head">Users in ${this._esc(this._customerName(tmpState.CustomerFilter))} &middot; most likely admins first</div>`;
			return tmpHeader + tmpState.Results.map((pUser) => this._resultRowHTML(pUser, false)).join('');
		}
		// No active search and nothing listed — prompt to search or list the scoped customer's users.
		if (!tmpSearching)
		{
			if (tmpState.CustomerFilter)
			{
				return `<div class="sug-hint">Type to search ${this._esc(this._customerName(tmpState.CustomerFilter))}, or <a class="sug-link" onclick="_Pict.providers.SuperUserGrant.loadCustomerUsers()">show all its users</a>.</div>`;
			}
			return `<div class="sug-hint">Type at least 2 characters to search across every customer, or pick a customer above to list its users.</div>`;
		}
		// Active search.
		if (!tmpState.Results.length) { return `<div class="sug-results-empty">No users match &ldquo;${this._esc(tmpQuery)}&rdquo;.</div>`; }
		return tmpState.Results.map((pUser) => this._resultRowHTML(pUser, true)).join('');
	}

	/** One result row: name + level/disabled chips, then email (and customer, in cross-customer search results). */
	_resultRowHTML(pUser, pShowCustomer)
	{
		const tmpState = this.state;
		const tmpActive = (tmpState.SelectedUser && (Number(tmpState.SelectedUser.IDUser) === Number(pUser.IDUser))) ? ' is-active' : '';
		const tmpLevel = this._levelLabel(pUser);
		const tmpLevelChip = tmpLevel ? `<span class="sug-result-tag">${this._esc(tmpLevel)}</span>` : '';
		const tmpDisabledChip = (pUser.Disabled === true || Number(pUser.Disabled) === 1) ? `<span class="sug-result-tag sug-result-tag-off">Disabled</span>` : '';
		const tmpMetaParts = [ pUser.Email || pUser.LoginID || '' ];
		if (pShowCustomer) { tmpMetaParts.push(this._customerName(pUser.IDCustomer)); }
		const tmpMeta = tmpMetaParts.filter((pPart) => pPart).join(' · ');
		return `<div class="sug-result${tmpActive}" onclick="_Pict.providers.SuperUserGrant.selectUser(${Number(pUser.IDUser)})">`
			+ `<div class="sug-result-name">${this._esc(libUserSearch.userLabel(pUser))}${tmpLevelChip}${tmpDisabledChip}</div>`
			+ `<div class="sug-result-meta">${this._esc(tmpMeta)}</div></div>`;
	}

	/** The user's base-level name (e.g. "Office Manager") for the result chip, or '' when unknown. */
	_levelLabel(pUser) { const tmpLvl = Number(pUser && pUser.IDRole); return Object.prototype.hasOwnProperty.call(USER_LEVEL_NAMES, tmpLvl) ? USER_LEVEL_NAMES[tmpLvl] : ''; }

	_detailHTML()
	{
		const tmpState = this.state;
		const tmpUser = tmpState.SelectedUser;
		if (!tmpUser) { return `<div class="sug-detail-empty">Search for a user on the left, then select them to manage their access.</div>`; }

		const tmpFacts = `<dl class="sug-facts">`
			+ `<dt>Email</dt><dd>${this._esc(tmpUser.Email || '—')}</dd>`
			+ `<dt>Customer</dt><dd>${this._esc(this._customerName(tmpUser.IDCustomer))}</dd>`
			+ `<dt>User ID</dt><dd>${Number(tmpUser.IDUser)}</dd>`
			+ `</dl>`;

		const tmpError = tmpState.Error ? `<p class="sug-error">${this._esc(tmpState.Error)}</p>` : '';
		const tmpNotice = tmpState.Notice ? `<p class="sug-ok">${this._esc(tmpState.Notice)}</p>` : '';

		return `<div class="sug-detail-name">${this._esc(libUserSearch.userLabel(tmpUser))}</div>`
			+ `<div class="sug-detail-sub">Elevate this user's access</div>`
			+ tmpFacts
			+ this._capPermissionGrantHTML(tmpUser)
			+ this._capConfigAdminHTML(tmpUser)
			+ tmpNotice + tmpError;
	}

	/** Capability card: Permission-Grant (the original bootstrap grant). */
	_capPermissionGrantHTML(pUser)
	{
		const tmpState = this.state;
		const tmpGranted = !!tmpState.GrantRecord;
		const tmpStatus = tmpState.GrantLoading
			? `<span class="sug-busy">checking…</span>`
			: (tmpGranted
				? `<span class="sug-status sug-status-granted">✔ Has Permission-Grant</span>`
				: `<span class="sug-status sug-status-none">No Permission-Grant</span>`);
		let tmpAction = '';
		if (tmpState.Busy) { tmpAction = `<span class="sug-busy">saving…</span>`; }
		else if (tmpState.GrantLoading) { tmpAction = ''; }
		else if (tmpGranted) { tmpAction = `<button class="sug-btn sug-btn-ghost" onclick="_Pict.providers.SuperUserGrant.revoke()">Remove Permission-Grant</button>`; }
		else { tmpAction = `<button class="sug-btn sug-btn-primary" onclick="_Pict.providers.SuperUserGrant.grant()">Grant Permission-Grant</button>`; }
		return `<div class="sug-cap"><div class="sug-cap-head"><span class="sug-cap-title">Permission granting</span>${tmpStatus}</div>`
			+ `<div class="sug-scope">The right to grant permissions within <strong>${this._esc(this._customerName(pUser.IDCustomer))}</strong> — the original bootstrap grant.</div>`
			+ `<div class="sug-actions">${tmpAction}</div></div>`;
	}

	/** Capability card: Customer configuration admin (assigns a PermissionLevel-'CustomerAdmin' group). */
	_capConfigAdminHTML(pUser)
	{
		const tmpState = this.state;
		const tmpHeld = !!tmpState.ConfigAdminHeld;
		const tmpStatus = tmpState.ConfigAdminLoading
			? `<span class="sug-busy">checking…</span>`
			: (tmpHeld
				? `<span class="sug-status sug-status-granted">✔ Can manage configuration</span>`
				: `<span class="sug-status sug-status-none">Cannot manage configuration</span>`);
		let tmpAction = '';
		if (tmpState.Busy) { tmpAction = `<span class="sug-busy">saving…</span>`; }
		else if (tmpState.ConfigAdminLoading) { tmpAction = ''; }
		else if (tmpHeld) { tmpAction = `<button class="sug-btn sug-btn-ghost" onclick="_Pict.providers.SuperUserGrant.revokeConfigAdmin()">Remove config admin</button>`; }
		else { tmpAction = `<button class="sug-btn sug-btn-primary" onclick="_Pict.providers.SuperUserGrant.grantConfigAdmin()">Make config admin</button>`; }
		return `<div class="sug-cap"><div class="sug-cap-head"><span class="sug-cap-title">Customer configuration admin</span>${tmpStatus}</div>`
			+ `<div class="sug-scope">Assigns a <strong>${this._esc(CONFIG_ADMIN_GROUP_NAME)}</strong> group (PermissionLevel <code>CustomerAdmin</code>) in <strong>${this._esc(this._customerName(pUser.IDCustomer))}</strong> — lets this user edit approval workflows and cascading configuration for their own customer.</div>`
			+ `<div class="sug-actions">${tmpAction}</div></div>`;
	}
}

module.exports = SuperUserGrantProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'SuperUserGrant',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
